import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BolDocumentPreview } from "@/components/orders/BolViewerDialog";

export interface NoteImageRef {
  id: string;
  file_path: string;
  file_name: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: NoteImageRef[];
  initialIndex: number;
}

/** Full-image preview for an order note attachment. Re-signs a fresh URL per view. */
export function OrderNoteImagePreviewDialog({ open, onOpenChange, images, initialIndex }: Props) {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  const current = images[index];

  if (!current) return null;
  const hasMany = images.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="truncate">
            {current.file_name ?? "Note image"}
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <BolDocumentPreview
            key={current.file_path}
            filePath={current.file_path}
            bucket="order-note-images"
          />
          {hasMany && (
            <>
              <Button
                size="icon"
                variant="secondary"
                className="absolute left-2 top-1/2 -translate-y-1/2 opacity-90"
                onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
                aria-label="Previous image"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="secondary"
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-90"
                onClick={() => setIndex((i) => (i + 1) % images.length)}
                aria-label="Next image"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs bg-background/80 rounded px-2 py-0.5">
                {index + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
